import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Alert,
  IconButton,
  Tooltip,
  Skeleton,
  Avatar,
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTimeOffBalances, useTimeOff } from '../../hooks/useTimeOff';
import { TimeOffRequest } from '../../types';
import RequestHistoryDialog from './RequestHistoryDialog';
import { useHolidays } from './HolidayManagement/useHolidays';

interface LeaveBalance {
  id: string;
  employeeId: string;
  vacationDaysTotal: number;
  vacationDaysUsed: number;
  sickDaysTotal: number;
  sickDaysUsed: number;
  personalDaysTotal: number;
  personalDaysUsed: number;
  year: number;
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    profilePicture?: string;
    user: {
      profilePicture?: string;
    };
  };
}

interface LeaveBalanceManagementProps {
  isAdmin: boolean;
}

const LeaveBalanceManagement: React.FC<LeaveBalanceManagementProps> = ({ isAdmin }) => {
  const { balances, isLoading: loading, error: apiError, updateBalance } = useTimeOffBalances();
  const { timeOffRequests } = useTimeOff();
  const { holidays } = useHolidays(isAdmin, new Date().getFullYear());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ [key: string]: { vacation: number; sick: number; personal: number } }>({});
  const [filterText, setFilterText] = useState('');
  const [requestHistoryOpen, setRequestHistoryOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<LeaveBalance | null>(null);
  const [requestHistory, setRequestHistory] = useState<TimeOffRequest[]>([]);

  // Handle API errors
  useEffect(() => {
    if (apiError) {
      setError((apiError as any).message || 'Failed to fetch leave balances');
    }
  }, [apiError]);

  const handleEdit = (balance: LeaveBalance) => {
    setEditingId(balance.id);
    setEditValues({
      [balance.id]: {
        vacation: balance.vacationDaysTotal,
        sick: balance.sickDaysTotal,
        personal: balance.personalDaysTotal
      }
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleSave = async (balance: LeaveBalance) => {
    try {
      const values = editValues[balance.id];
      if (!values) return;

      await updateBalance({
        employeeId: balance.employeeId,
        data: {
          vacationDaysTotal: values.vacation,
          sickDaysTotal: values.sick,
          personalDaysTotal: values.personal
        }
      });

      setEditingId(null);
      setEditValues({});
      setSuccess('Leave balance updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update leave balance');
    }
  };

  const handleInputChange = (balanceId: string, field: 'vacation' | 'sick' | 'personal', value: string) => {
    const numValue = parseInt(value) || 0;
    setEditValues(prev => ({
      ...prev,
      [balanceId]: {
        ...prev[balanceId],
        [field]: numValue
      }
    }));
  };

  const handleRowClick = (balance: LeaveBalance) => {
    setSelectedEmployee(balance);
    setRequestHistoryOpen(true);
    
    // Filter requests from already-fetched data
    const currentYear = new Date().getFullYear();
    const employeeRequests = timeOffRequests.filter((req: any) => 
      req.employee.id === balance.employeeId && 
      new Date(req.startDate).getFullYear() === currentYear
    );
    
    setRequestHistory(employeeRequests);
  };

  const handleCloseRequestHistory = () => {
    setRequestHistoryOpen(false);
    setSelectedEmployee(null);
    setRequestHistory([]);
  };

  // Filter balances based on search text
  const filteredBalances = balances.filter((balance: LeaveBalance) => {
    const fullName = `${balance.employee.lastName} ${balance.employee.firstName}`.toLowerCase();
    const searchText = filterText.toLowerCase();
    return fullName.includes(searchText);
  });

  if (!isAdmin) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">
            Access denied. Only administrators can manage leave balances.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Leave Balance Management</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Vacation Days</TableCell>
                  <TableCell>Sick Days</TableCell>
                  <TableCell>Personal Days</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton variant="text" width={120} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} /></TableCell>
                    <TableCell><Skeleton variant="text" width={60} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>Users</Typography>
            <Typography variant="body2" color="text.secondary">
              Click on a user row to view their request history for the current year.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Search Filter */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by employee name..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
              ),
            }}
          />
        </Box>

        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Vacation Days</TableCell>
                <TableCell>Sick Days</TableCell>
                <TableCell>Personal Days</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBalances.map((balance: LeaveBalance) => {
                const isEditing = editingId === balance.id;
                const values = editValues[balance.id];

                return (
                  <TableRow 
                    key={balance.id}
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                    onClick={() => handleRowClick(balance)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar 
                          sx={{ width: 32, height: 32, fontSize: '0.875rem' }}
                          src={balance.employee.user?.profilePicture || balance.employee.profilePicture || undefined}
                        >
                          {balance.employee.firstName[0]}{balance.employee.lastName[0]}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {balance.employee.lastName} {balance.employee.firstName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={values?.vacation || balance.vacationDaysTotal}
                          onChange={(e) => handleInputChange(balance.id, 'vacation', e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          inputProps={{ min: 0 }}
                          sx={{ width: 80 }}
                        />
                      ) : (
                        <Typography 
                          variant="body1" 
                          color="primary"
                          sx={{ fontWeight: 700 }}
                        >
                          {balance.vacationDaysUsed}/{balance.vacationDaysTotal}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={values?.sick || balance.sickDaysTotal}
                          onChange={(e) => handleInputChange(balance.id, 'sick', e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          inputProps={{ min: 0 }}
                          sx={{ width: 80 }}
                        />
                      ) : (
                        <Typography 
                          variant="body1" 
                          color="error"
                          sx={{ fontWeight: 700 }}
                        >
                          {balance.sickDaysUsed}/{balance.sickDaysTotal}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={values?.personal || balance.personalDaysTotal}
                          onChange={(e) => handleInputChange(balance.id, 'personal', e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          inputProps={{ min: 0 }}
                          sx={{ width: 80 }}
                        />
                      ) : (
                        <Typography 
                          variant="body1" 
                          color="primary"
                          sx={{ fontWeight: 700 }}
                        >
                          {balance.personalDaysUsed}/{balance.personalDaysTotal}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Save">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSave(balance);
                              }}
                            >
                              <SaveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancel();
                              }}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(balance);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredBalances.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {filterText ? 'No employees found matching your search.' : 'No leave balances found for the current year.'}
            </Typography>
          </Box>
        )}

        <RequestHistoryDialog
          open={requestHistoryOpen}
          onClose={handleCloseRequestHistory}
          selectedEmployee={selectedEmployee}
          requestHistory={requestHistory}
          loadingHistory={false}
          holidays={holidays}
        />
      </CardContent>
    </Card>
  );
};

export default LeaveBalanceManagement;
